import { createSignal, createEffect, createMemo, createResource, on, onMount, onCleanup, untrack, Show, For, Switch, Match } from 'solid-js';
import { A, useSearchParams } from '@solidjs/router';
import { useApi, isOk } from '../api';
import type { SelfShutdownIncidentDto } from '../api';
import { selfShutoffTriggerKey, selfShutoffAcknowledgedRevision } from '../services/selfShutoff';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import { subscribeBotEngineEvents, subscribeBotEngineStream, reconnectBotEngineStream, type BotEngineEvent, type BotEngineStreamState } from '../services/botEngineEvents';
import Text from '../components/Text';
import { Skeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import FeatureGate from '../components/FeatureGate';
import LicenseGate from '../components/LicenseGate';
import { GrantTypes } from '../services/grantsApi';
import ChatLog from '../components/ChatLog';
import BotInventory from '../components/BotInventory';
import type { InventorySnapshot, SlotRef } from '../models/inventorySlot';
import DumpInventoryPanel from '../components/DumpInventoryPanel';
import PlayerState, { type PlayerStateSnapshot } from '../components/PlayerState';
import ProfileInfoCard from '../components/ProfileInfoCard';
import Modal from '../components/Modal';
import { LiveView } from './LiveView';
import { CraftingResolverService } from '../services/craftingResolver';
import ProxySelector from '../components/ProxySelector';
import { useActiveBot } from '../state/activeBot';
import { getSharedResolver } from './botengine/sharedResolver';
import {
    CHAT_MAX_LENGTH,
    type BotStatus,
    type SessionInfo,
    type PluginTasks,
    type TaskNode,
    type QueuedTask,
} from './botengine/types';
import { formatDateTime, formatDateTimeShort, formatTime as fmtTime } from '../services/dateFormat';
import { BOT_ACTIONS, can } from '../services/resourceActions';
import { useBotFanOut } from '../hooks/useBotFanOut';
import GroupScopeBar from '../components/GroupScopeBar';
import { downloadFromApi } from '../services/downloadFile';
import styles from './BotEngine.module.css';
import { firstError } from '../api/envelope';
import TaskParameterInput from '../components/TaskParameterInput';
import type { BotConnectionMarkerDto, BotTaskHistoryPageResponse } from '../api/generated/schemas';

const TASK_HISTORY_CAP = 200;

function taskNodeScalarsEqual(a: TaskNode, b: TaskNode): boolean {
    return a.status === b.status
        && a.progress === b.progress
        && a.statusMessage === b.statusMessage
        && a.error === b.error
        && a.isPaused === b.isPaused
        && a.interceptDepth === b.interceptDepth
        && a.completedAt === b.completedAt
        && a.taskName === b.taskName
        && a.pluginName === b.pluginName;
}

// Building a fresh object graph on every task_update would make <For> remount
// every subtask row each tick, since it diffs by array-item reference. Reuse
// the previous node (and its subtask references) wherever nothing changed.
function mergeTaskNode(prev: TaskNode | null, next: TaskNode): TaskNode {
    if (!prev || prev.id !== next.id) return next;

    const mergedSubtasks = next.subtasks.map(sub => {
        const prevSub = prev.subtasks.find(s => s.id === sub.id) ?? null;
        return mergeTaskNode(prevSub, sub);
    });
    let subtasksChanged = mergedSubtasks.length !== prev.subtasks.length;
    if (!subtasksChanged) {
        for (let i = 0; i < mergedSubtasks.length; i++) {
            if (mergedSubtasks[i] !== prev.subtasks[i]) { subtasksChanged = true; break; }
        }
    }

    const mergedIntercept = next.interceptTask
        ? mergeTaskNode(prev.interceptTask ?? null, next.interceptTask)
        : undefined;
    const interceptChanged = mergedIntercept !== prev.interceptTask;

    if (!subtasksChanged && !interceptChanged && taskNodeScalarsEqual(prev, next)) {
        return prev;
    }
    return { ...next, subtasks: mergedSubtasks, interceptTask: mergedIntercept };
}

export default function BotEngine() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();

    const [searchParams] = useSearchParams();
    const { botId: activeBotId, bot: activeBot, setBot, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();
    const focusBotId = () => (searchParams.bot as string | undefined) ?? undefined;

    createEffect(() => {
        const f = focusBotId();
        if (f && activeBotId() !== f) setBot(f);
    });

    const selectedBotId = () => activeBotId() ?? '';
    const mine = () => activeBot()?.myActions;
    const canControl = () => can(mine(), BOT_ACTIONS.Control);
    const canWorldWrite = () => can(mine(), BOT_ACTIONS.WorldWrite);
    const canRunTasks = () => can(mine(), BOT_ACTIONS.TasksRun);
    const canManageCredentials = () => can(mine(), BOT_ACTIONS.CredentialsManage);
    const canAssignProxy = () => can(mine(), BOT_ACTIONS.ProxyAssign);

    const [botStatus, setBotStatus] = createSignal<BotStatus>({ running: false });
    const [disconnectReason, setDisconnectReason] = createSignal<string | null>(null);
    const [session, setSession] = createSignal<SessionInfo>({ linked: false });
    const [serverAddress, setServerAddress] = createSignal(localStorage.getItem('botengine_server') || '');
    const [serverPort, setServerPort] = createSignal(Number(localStorage.getItem('botengine_port')) || 25565);
    const [command, setCommand] = createSignal('');
    const [loading, setLoading] = createSignal(false);
    const [authLoading, setAuthLoading] = createSignal(false);
    const [chatLog, setChatLog] = createSignal<string[]>([]);
    const [consoleLog, setConsoleLog] = createSignal<string[]>([]);
    const [shutoffIncidents, setShutoffIncidents] = createSignal<SelfShutdownIncidentDto[]>([]);
    const [availableTasks, setAvailableTasks] = createSignal<PluginTasks[]>([]);
    const [activeTask, setActiveTask] = createSignal<TaskNode | null>(null);
    const [taskArgs, setTaskArgs] = createSignal<Record<string, Record<string, string>>>({});
    const [taskLoading, setTaskLoading] = createSignal(false);
    const [taskQueue, setTaskQueue] = createSignal<QueuedTask[]>([]);
    const [chatCommands, setChatCommands] = createSignal<TaskNode[]>([]);
    const [taskHistory, setTaskHistory] = createSignal<TaskNode[]>([]);
    const [taskHistoryNextBeforeId, setTaskHistoryNextBeforeId] = createSignal<string | null>(null);
    const [taskHistoryCursor, setTaskHistoryCursor] = createSignal<string | undefined>(undefined);
    const storedTaskRunIds = new Set<string>();
    const [connectionMarkers, setConnectionMarkers] = createSignal<BotConnectionMarkerDto[]>([]);
    const [liveViewOpen, setLiveViewOpen] = createSignal(false);
    const [inventory, setInventory] = createSignal<InventorySnapshot | null>(null);
    const [playerState, setPlayerState] = createSignal<PlayerStateSnapshot | null>(null);
    const [resolverReady, setResolverReady] = createSignal<CraftingResolverService | null>(null);
    type BotEngineTab = 'live' | 'automation';
    const TAB_STORAGE_KEY = 'botengine_active_tab_v1';
    const [activeTab, setActiveTab] = createSignal<BotEngineTab>(
        (localStorage.getItem(TAB_STORAGE_KEY) as BotEngineTab | null) ?? 'live'
    );
    createEffect(() => {
        localStorage.setItem(TAB_STORAGE_KEY, activeTab());
    });
    const [expandedPlugin, setExpandedPlugin] = createSignal<string | null>(null);

    const [resumeBehavior, setResumeBehavior] = createSignal<0 | 1 | 2>(1);
    const [resumePinnedKey, setResumePinnedKey] = createSignal<string>('');
    const [resumePinnedArgs, setResumePinnedArgs] = createSignal<Record<string, string>>({});
    const [resumeLastKey, setResumeLastKey] = createSignal<string | null>(null);
    const [resumeLastAt, setResumeLastAt] = createSignal<string | null>(null);
    const [resumeSaving, setResumeSaving] = createSignal(false);

    const [selectedTaskId, setSelectedTaskId] = createSignal<string | null>(null);

    const [taskExpandOverride, setTaskExpandOverride] = createSignal<Record<string, boolean>>({});
    const isTerminal = (s: string) => s === 'Completed' || s === 'Failed' || s === 'Cancelled';
    const nodeExpanded = (node: TaskNode) => {
        const override = taskExpandOverride()[node.id];
        if (override !== undefined) return override;
        return !isTerminal(node.status);
    };
    const toggleNode = (node: TaskNode) => {
        const next = !nodeExpanded(node);
        setTaskExpandOverride((prev) => ({ ...prev, [node.id]: next }));
    };
    const subtaskStats = (node: TaskNode) => {
        const total = node.subtasks.length;
        const done = node.subtasks.filter((s) => isTerminal(s.status)).length;
        const failed = node.subtasks.some((s) => s.status === 'Failed');
        return { total, done, failed };
    };
    const setSubtreeExpanded = (root: TaskNode | null, value: boolean) => {
        if (!root) return;
        const next: Record<string, boolean> = { ...taskExpandOverride() };
        const walk = (node: TaskNode) => {
            if (node.subtasks.length > 0) next[node.id] = value;
            node.subtasks.forEach(walk);
        };
        walk(root);
        setTaskExpandOverride(next);
    };

    const findTaskById = (root: TaskNode | null, id: string): TaskNode | null => {
        if (!root) return null;
        if (root.id === id) return root;
        for (const sub of root.subtasks) {
            const found = findTaskById(sub, id);
            if (found) return found;
        }
        return null;
    };
    const selectedTask = createMemo<TaskNode | null>(() => {
        const id = selectedTaskId();
        if (!id) return null;
        const fromActive = findTaskById(activeTask(), id);
        if (fromActive) return fromActive;
        for (const entry of taskHistory()) {
            const found = findTaskById(entry, id);
            if (found) return found;
        }
        for (const entry of chatCommands()) {
            const found = findTaskById(entry, id);
            if (found) return found;
        }
        return null;
    });

    const [streamState, setStreamState] = createSignal<BotEngineStreamState>('connecting');
    const [streamEpoch, setStreamEpoch] = createSignal(0);
    const [lastLiveAt, setLastLiveAt] = createSignal<number | null>(null);

    const fetchStatus = async () => {
        if (!selectedBotId()) return;
        const res = await api.botLifecycle.getStatus(selectedBotId()!);
        if (!isOk(res.data)) return;
        const v = res.data.value as any;
        if (v?.state === 'Reconnecting') {
            setBotStatus({ running: false, state: 'Reconnecting', username: v.username, server: v.server });
        } else if (v?.state === 'Disconnected' || v?.state === 'Error') {
            setBotStatus({ running: false, state: v.state });
        } else {
            setBotStatus({
                ...v,
                running: v?.state === 'Playing',
            });
        }
    };

    const fetchAuthStatus = async () => {
        if (!selectedBotId()) return;
        const res = await api.botAuth.authStatus(selectedBotId()!);
        if (isOk(res.data)) setSession(res.data.value as unknown as SessionInfo);
    };

    const expiryText = () => {
        const exp = session().tokenExpiry;
        if (!exp) return '';
        const diff = new Date(exp).getTime() - Date.now();
        if (diff <= 0) return 'Expired';
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const fetchPlayerState = async () => {
        if (!selectedBotId()) return;
        const res = await api.botLifecycle.getPlayerState(selectedBotId()!);
        if (!isOk(res.data) || !(res.data.value as any)?.running) {
            setPlayerState(null);
            return;
        }
        setPlayerState((res.data.value as any).state);
    };

    const fetchInventory = async () => {
        if (!selectedBotId()) return;
        const res = await api.botLifecycle.getInventory(selectedBotId()!);
        if (!isOk(res.data) || !(res.data.value as any)?.running) {
            setInventory(null);
            return;
        }
        const v = res.data.value as any;
        setInventory({
            activeHotbar: v.activeHotbar ?? 0,
            slots: v.slots ?? [],
            cursor: v.cursor ?? { itemId: -1, count: 0, damage: 0 },
            window: v.window
                ? {
                    id: v.window.id,
                    type: v.window.type ?? '',
                    title: v.window.title ?? '',
                    slotCount: v.window.slotCount ?? 0,
                    slots: v.window.slots ?? [],
                }
                : null,
        });
    };

    const fetchChatLog = async () => {
        if (!selectedBotId()) return;
        const res = await api.botLifecycle.getChatLog(selectedBotId()!);
        if (isOk(res.data)) setChatLog((res.data.value as any).messages ?? []);
    };

    const fetchConsoleLog = async () => {
        if (!selectedBotId()) return;
        const res = await api.botLifecycle.getConsoleLog(selectedBotId()!);
        if (isOk(res.data)) setConsoleLog((res.data.value as any).messages ?? []);
    };

    // Acknowledged incidents stay in this list: once acknowledged they leave the banner, and this is
    // where an owner looks back at what took the bot down.
    const openShutoffIncidents = createMemo(() => shutoffIncidents().filter((incident) => !incident.acknowledgedAtUtc));
    const acknowledgedShutoffIncidents = createMemo(() => shutoffIncidents().filter((incident) => incident.acknowledgedAtUtc));

    const shutoffIncidentRow = (incident: SelfShutdownIncidentDto) => (
        <div class={styles.selfShutoffRow}>
            <div class="v" style={{ gap: '2px', 'min-width': 0 }}>
                <span class={styles.selfShutoffReason}>{t(selfShutoffTriggerKey(incident.trigger))}</span>
                <span class={styles.selfShutoffMeta}>
                    {formatDateTime(incident.occurredAtUtc)}
                    <Show when={incident.lastRunningTaskKey}>
                        {' '}&middot; {incident.lastRunningTaskKey}
                    </Show>
                </span>
            </div>
            <div class="h" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center' }}>
                <span class={`${styles.selfShutoffState} ${incident.acknowledgedAtUtc ? styles.selfShutoffAcked : styles.selfShutoffOpen}`}>
                    <Text val={incident.acknowledgedAtUtc ? 'BOTENGINE_SELF_SHUTOFF_ACKED' : 'BOTENGINE_SELF_SHUTOFF_OPEN'} />
                </span>
                <A class="btn btn-secondary btn-sm" href={`/selfshutoff/${incident.id}`}>
                    <Text val="SELF_SHUTOFF_DETAILS" />
                </A>
            </div>
        </div>
    );

    const fetchShutoffIncidents = async () => {
        if (!selectedBotId()) return;
        const res = await api.botSelfShutdown.listForBot(selectedBotId()!);
        if (isOk(res.data)) setShutoffIncidents(res.data.value.incidents ?? []);
    };

    const fetchTasks = async () => {
        if (!selectedBotId()) return;
        const res = await api.botTask.getAvailableTasks(selectedBotId()!);
        if (isOk(res.data)) setAvailableTasks(((res.data.value as any).available ?? []) as PluginTasks[]);
    };

    let taskStatusSeq = 0;
    const fetchTaskStatus = async () => {
        if (!selectedBotId()) return;
        const issued = ++taskStatusSeq;
        const res = await api.botTask.getTaskStatus(selectedBotId()!);
        // A slower earlier response must not land on top of a newer one.
        if (issued !== taskStatusSeq) return;
        if (isOk(res.data)) {
            const v = res.data.value as any;
            setActiveTask(prev => v.active ? mergeTaskNode(prev, v.tree) : null);
            setTaskQueue(v.queue ?? []);
            setChatCommands(v.chatCommands ?? []);
        }
    };

    const fetchTaskHistoryPage = async (botId: string, beforeId?: string) => {
        const res = await api.botTask.getTaskHistory(botId, beforeId ? { beforeId } : undefined);
        return isOk(res.data) ? res.data.value! : null;
    };

    // Paging, the stream resync and live events all land here, so none of them can drop what another added.
    const mergeTaskHistory = (runs: TaskNode[], stored: boolean) => {
        if (stored) runs.forEach(run => storedTaskRunIds.add(run.id));
        const runsById = new Map(taskHistory().map(run => [run.id, run]));
        for (const run of runs) runsById.set(run.id, run);
        const newestFirst = [...runsById.values()].sort((a, b) =>
            (Date.parse(b.completedAt ?? '') || 0) - (Date.parse(a.completedAt ?? '') || 0) || b.id.localeCompare(a.id));
        if (newestFirst.length <= TASK_HISTORY_CAP) {
            setTaskHistory(newestFirst);
            return;
        }
        const keptRuns = newestFirst.slice(0, TASK_HISTORY_CAP);
        setTaskHistory(keptRuns);
        const oldestKeptStoredRun = [...keptRuns].reverse().find(run => storedTaskRunIds.has(run.id));
        if (oldestKeptStoredRun) setTaskHistoryNextBeforeId(oldestKeptStoredRun.id);
    };

    const mergeConnectionMarkers = (markers: BotConnectionMarkerDto[]) => {
        if (markers.length === 0) return;
        const markersById = new Map(connectionMarkers().map(marker => [marker.id, marker]));
        for (const marker of markers) markersById.set(marker.id, marker);
        setConnectionMarkers([...markersById.values()]);
    };

    const mergeTaskHistoryPage = (page: BotTaskHistoryPageResponse) => {
        mergeTaskHistory(page.items as TaskNode[], true);
        mergeConnectionMarkers(page.connectionMarkers);
    };

    const isConnectionMarker = (entry: TaskNode | BotConnectionMarkerDto): entry is BotConnectionMarkerDto => 'online' in entry;

    const taskHistoryTimeline = createMemo<(TaskNode | BotConnectionMarkerDto)[]>(() => {
        const runs = taskHistory();
        const oldestShownRunAt = taskHistoryNextBeforeId() && runs.length > 0
            ? Date.parse(runs[runs.length - 1].completedAt ?? '') || 0
            : -Infinity;
        const markersOldestFirst = [...connectionMarkers()].sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
        // A dropped connection is logged as Disconnected and again as Reconnecting; one marker per side is enough.
        const transitions = markersOldestFirst.filter((marker, index) =>
            (index === 0 || markersOldestFirst[index - 1].online !== marker.online) && Date.parse(marker.at) >= oldestShownRunAt);
        const entryAt = (entry: TaskNode | BotConnectionMarkerDto) =>
            Date.parse((isConnectionMarker(entry) ? entry.at : entry.completedAt) ?? '') || 0;
        return [...runs, ...transitions].sort((a, b) => entryAt(b) - entryAt(a));
    });

    const [taskHistoryPage, { refetch: retryTaskHistoryPage }] = createResource(
        () => selectedBotId() ? { botId: selectedBotId()!, beforeId: taskHistoryCursor() } : false,
        async (request) => {
            const page = await fetchTaskHistoryPage(request.botId, request.beforeId);
            if (!page) throw new Error(t('BOTENGINE_TASK_HISTORY_LOAD_FAILED'));
            return page;
        });

    createEffect(() => {
        if (taskHistoryPage.state !== 'ready') return;
        const page = taskHistoryPage();
        untrack(() => {
            setTaskHistoryNextBeforeId(page.nextBeforeId ?? null);
            mergeTaskHistoryPage(page);
        });
    });

    createEffect(() => {
        if (taskHistoryPage.state === 'errored' && untrack(taskHistoryCursor))
            toast?.error(t('BOTENGINE_TASK_HISTORY_LOAD_FAILED'));
    });

    const loadOlderTaskHistory = () => {
        const nextBeforeId = taskHistoryNextBeforeId();
        if (!nextBeforeId) return;
        if (taskHistoryCursor() === nextBeforeId) void retryTaskHistoryPage();
        else setTaskHistoryCursor(nextBeforeId);
    };

    // The stream dropped events, so a finished run may have no task_history_added behind it.
    const resyncTaskHistory = async () => {
        const botId = selectedBotId();
        if (!botId) return;
        const page = await fetchTaskHistoryPage(botId);
        if (!page || botId !== selectedBotId()) return;
        mergeTaskHistoryPage(page);
    };

    // A running task emits progress several times a second; one request per tick buried
    // the endpoint and only ever mattered for the queue and chat-command lists, which the
    // task_update payload does not carry.
    let taskStatusTimer: number | undefined;
    const scheduleTaskStatus = () => {
        if (taskStatusTimer !== undefined) return;
        taskStatusTimer = window.setTimeout(() => {
            taskStatusTimer = undefined;
            void fetchTaskStatus();
        }, 750);
    };
    onCleanup(() => { if (taskStatusTimer !== undefined) clearTimeout(taskStatusTimer); });

    const cancelQueuedTask = async (queueId: string) => {
        const res = await api.botTask.cancelQueuedTask(selectedBotId()!, queueId);
        if (isOk(res.data)) {
            fetchTaskStatus();
        } else {
            toast?.error(firstError(res.data) || t('BOTENGINE_CANCEL_QUEUE_FAILED'));
        }
    };

    const fetchResumeConfig = async () => {
        if (!selectedBotId()) return;
        const res = await api.botLifecycle.getResumeConfig(selectedBotId()!);
        if (!isOk(res.data)) return;
        const v = res.data.value as {
            behavior: number;
            pinnedTaskKey?: string | null;
            pinnedTaskArgs?: Record<string, string> | null;
            lastRunningTaskKey?: string | null;
            lastSnapshotAt?: string | null;
        };
        setResumeBehavior((v.behavior ?? 1) as 0 | 1 | 2);
        setResumePinnedKey(v.pinnedTaskKey ?? '');
        setResumePinnedArgs(v.pinnedTaskArgs ?? {});
        setResumeLastKey(v.lastRunningTaskKey ?? null);
        setResumeLastAt(v.lastSnapshotAt ?? null);
    };

    const saveResumeConfig = async () => {
        if (!selectedBotId()) return;
        setResumeSaving(true);
        const body: any = { behavior: resumeBehavior() };
        if (resumeBehavior() === 2) {
            body.pinnedTaskKey = resumePinnedKey();
            body.pinnedTaskArgs = resumePinnedArgs();
        }
        const res = await api.botLifecycle.setResumeConfig(selectedBotId()!, body);
        setResumeSaving(false);
        if (isOk(res.data)) {
            toast?.info(t('BOTENGINE_RESUME_SAVED') || 'Saved');
            fetchResumeConfig();
        } else {
            toast?.error(firstError(res.data) ?? t('BOTENGINE_RESUME_SAVE_FAILED') ?? 'Save failed');
        }
    };

    const setResumePinnedArg = (param: string, value: string) => {
        setResumePinnedArgs(prev => ({ ...prev, [param]: value }));
    };

    const startTask = async (plugin: string, task: string) => {
        const key = `${plugin}:${task}`;
        const args = taskArgs()[key] ?? {};
        setTaskLoading(true);
        const res = await api.botTask.runTask(selectedBotId()!, { plugin, task, args: args as never });
        setTaskLoading(false);
        if (!isOk(res.data)) toast?.error(firstError(res.data) || t('BOTENGINE_START_TASK_FAILED'));
    };

    const enqueueTask = async (plugin: string, task: string) => {
        const key = `${plugin}:${task}`;
        const args = taskArgs()[key] ?? {};
        setTaskLoading(true);
        const res = await api.botTask.enqueueTask(selectedBotId()!, { plugin, task, args: args as never });
        setTaskLoading(false);
        if (isOk(res.data)) {
            fetchTaskStatus();
        } else {
            toast?.error(firstError(res.data) || t('BOTENGINE_ENQUEUE_TASK_FAILED'));
        }
    };

    const cancelTask = async () => {
        setTaskLoading(true);
        const res = await api.botTask.cancelTask(selectedBotId()!);
        setTaskLoading(false);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) || t('BOTENGINE_CANCEL_TASK_FAILED'));
        }
    };

    const [pauseLoading, setPauseLoading] = createSignal(false);
    const togglePause = async () => {
        const task = activeTask();
        if (!task) return;
        setPauseLoading(true);
        const res = task.isPaused
            ? await api.botTask.resumeTask(selectedBotId()!)
            : await api.botTask.pauseTask(selectedBotId()!);
        setPauseLoading(false);
        if (!isOk(res.data)) {
            toast?.error(firstError(res.data) || t('BOTENGINE_TOGGLE_PAUSE_FAILED'));
        }
    };

    const setTaskArg = (plugin: string, task: string, param: string, value: string) => {
        const key = `${plugin}:${task}`;
        setTaskArgs(prev => ({
            ...prev,
            [key]: { ...(prev[key] ?? {}), [param]: value }
        }));
    };

    const handleSSEEvent = (evt: BotEngineEvent) => {
        switch (evt.type) {
            case 'status_changed': {
                const p = evt.payload;
                if (p.state === 'Disconnected' || p.state === 'Error') {
                    setBotStatus({ running: false });
                    setActiveTask(null);
                    setTaskQueue([]);
                    setChatCommands([]);
                    setAvailableTasks([]);
                    if (p.state === 'Error') {
                        toast?.error(t('BOTENGINE_BOT_ERROR') || 'Bot encountered an error');
                    }
                } else if (p.state === 'Reconnecting') {
                    setBotStatus({ running: false, state: 'Reconnecting', username: p.username, server: p.server });
                    setActiveTask(null);
                    setTaskQueue([]);
                    setChatCommands([]);
                    setAvailableTasks([]);
                } else {
                    if (p.state === 'Playing') setDisconnectReason(null);
                    setBotStatus({
                        running: p.state === 'Playing',
                        state: p.state,
                        username: p.username,
                        server: p.server,
                        connectedSince: p.connectedSince,
                        ping: p.ping,
                        packetsInPerSecond: p.packetsInPerSecond,
                        packetsOutPerSecond: p.packetsOutPerSecond,
                        chunksLoaded: p.chunksLoaded,
                        entitiesTracked: p.entitiesTracked,
                    });
                }
                break;
            }
            case 'kicked': {
                const reason = evt.payload?.reason || (t('BOTENGINE_KICKED') || 'Bot was kicked from the server');
                toast?.warning(reason);
                setDisconnectReason(reason);
                setBotStatus({ running: false });
                setTaskQueue([]);
                break;
            }
            case 'stats_update':
                break;
            case 'chat_message':
                setChatLog(prev => {
                    const next = [...prev, evt.payload.message];
                    return next.length > 200 ? next.slice(-200) : next;
                });
                break;
            case 'console_log':
                setConsoleLog(prev => {
                    const next = [...prev, evt.payload.message];
                    return next.length > 200 ? next.slice(-200) : next;
                });
                break;
            case 'inventory_update': {
                const p = evt.payload;
                setInventory(prev => ({
                    activeHotbar: p.activeHotbar ?? 0,
                    slots: p.slots ?? [],
                    cursor: p.cursor ?? { itemId: -1, count: 0, damage: 0 },
                    window: prev?.window ?? null,
                }));
                break;
            }
            case 'window_update': {
                const w = evt.payload;
                setInventory(prev => ({
                    activeHotbar: prev?.activeHotbar ?? 0,
                    slots: prev?.slots ?? [],
                    cursor: prev?.cursor ?? { itemId: -1, count: 0, damage: 0 },
                    window: {
                        id: w.id,
                        type: w.type ?? '',
                        title: w.title ?? '',
                        slotCount: w.slotCount ?? 0,
                        slots: w.slots ?? [],
                    },
                }));
                break;
            }
            case 'window_closed': {
                setInventory(prev => prev ? { ...prev, window: null } : prev);
                break;
            }
            case 'player_state': {
                setPlayerState(evt.payload as PlayerStateSnapshot);
                break;
            }
            case 'task_update': {
                const task: TaskNode | null = evt.payload ?? null;
                setActiveTask(prev => task ? mergeTaskNode(prev, task) : null);
                scheduleTaskStatus();
                break;
            }
            case 'task_history_added': {
                const entry = evt.payload as TaskNode | null;
                if (entry) {
                    mergeTaskHistory([entry], false);
                }
                break;
            }
            case 'connection_marker_added': {
                const marker = evt.payload as BotConnectionMarkerDto | null;
                if (marker) mergeConnectionMarkers([marker]);
                break;
            }
        }
    };

    onMount(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.has('linked')) {
            toast?.info(t('BOTENGINE_LINK_SUCCESS') || 'Microsoft account linked successfully');
            window.history.replaceState({}, '', window.location.pathname);
        } else if (params.has('error')) {
            const err = params.get('error');
            const messages: Record<string, string> = {
                'invalid_state': t('BOTENGINE_ERROR_INVALID_STATE') || 'Authentication failed: invalid or expired session. Please try again.',
                'user_mismatch': t('BOTENGINE_ERROR_USER_MISMATCH') || 'Authentication failed: user mismatch.',
                'auth_failed': t('BOTENGINE_ERROR_AUTH_FAILED') || 'Authentication failed. Please try again.',
            };
            toast?.error(messages[err!] || messages['auth_failed']);
            window.history.replaceState({}, '', window.location.pathname);
        }

        getSharedResolver().then(setResolverReady).catch(() => { });
    });

    // Memo on boolean: raw botStatus() identity churns every 5s and steals input focus in task <For>.
    const isReconnecting = createMemo(() => botStatus().state === 'Reconnecting');
    const isConnecting = createMemo(() => botStatus().state === 'Connecting');
    const isRunning = createMemo(() => botStatus().running);

    const fetchRunningState = async () => {
        if (!isRunning()) {
            setInventory(null);
            setPlayerState(null);
            return;
        }
        await Promise.all([
            fetchTasks(),
            fetchTaskStatus(),
            fetchChatLog(),
            fetchConsoleLog(),
            fetchShutoffIncidents(),
            fetchInventory(),
            fetchPlayerState(),
            fetchResumeConfig(),
        ]);
    };

    const fetchBotAndSession = async () => {
        if (!selectedBotId()) return;
        await Promise.all([fetchStatus(), fetchAuthStatus()]);
    };

    createEffect(on(selectedBotId, (botId) => {
        setBotStatus({ running: false });
        setDisconnectReason(null);
        setSession({ linked: false });
        setChatLog([]);
        setConsoleLog([]);
        setActiveTask(null);
        setTaskQueue([]);
        setChatCommands([]);
        setAvailableTasks([]);
        setTaskHistory([]);
        setTaskHistoryNextBeforeId(null);
        setTaskHistoryCursor(undefined);
        storedTaskRunIds.clear();
        setConnectionMarkers([]);
        setInventory(null);
        setPlayerState(null);
        setStreamState('connecting');
        setStreamEpoch(0);
        setLastLiveAt(null);
        if (!botId) return;

        void fetchBotAndSession();
        const unsubscribeEvents = subscribeBotEngineEvents(botId, handleSSEEvent);
        const unsubscribeStream = subscribeBotEngineStream(botId, (status) => {
            setStreamState(status.state);
            setLastLiveAt(status.lastMessageAt);
            setStreamEpoch(status.epoch);
        });
        onCleanup(() => { unsubscribeEvents(); unsubscribeStream(); });
    }));

    // A rising epoch means the stream restarted or the server dropped events for this
    // subscriber, and nothing replays either. Deferred and created after the subscribe
    // effect so an already-live stream's current epoch does not refetch what that effect
    // just fetched.
    createEffect(on(streamEpoch, () => { void fetchBotAndSession(); void resyncTaskHistory(); }, { defer: true }));
    createEffect(on(selfShutoffAcknowledgedRevision, () => { void fetchShutoffIncidents(); }, { defer: true }));

    // Both triggers load the same set, so they share one effect: the bot going live gets
    // it for the first time, a stream gap gets it again because the events that would have
    // kept it current are gone.
    createEffect(on([isRunning, streamEpoch], () => { void fetchRunningState(); }));

    const startBot = async () => {
        if (!serverAddress()) {
            toast?.warning(t('BOTENGINE_ENTER_SERVER') || 'Enter a server address');
            return;
        }
        const port = serverPort();
        if (port < 1 || port > 65535) {
            toast?.warning(t('BOTENGINE_INVALID_PORT') || 'Port must be between 1 and 65535');
            return;
        }
        localStorage.setItem('botengine_server', serverAddress());
        localStorage.setItem('botengine_port', String(port));
        setTaskQueue([]);
        setChatCommands([]);
        setActiveTask(null);
        setPlayerState(null);
        setInventory(null);
        setDisconnectReason(null);
        setLoading(true);
        await fanOut.run(targetIds(BOT_ACTIONS.Control),
            targetBotId => api.botLifecycle.startBot(targetBotId, { server: serverAddress(), port }),
            { success: t('BOTENGINE_STARTED_ON', serverAddress()), failure: t('BOTENGINE_START_FAILED') });
        setLoading(false);
    };

    const stopBot = async () => {
        setLoading(true);
        const failedIds = await fanOut.run(targetIds(BOT_ACTIONS.Control),
            targetBotId => api.botLifecycle.stopBot(targetBotId),
            { success: t('BOTENGINE_STOPPED'), failure: t('BOTENGINE_STOP_FAILED') });
        setLoading(false);
        if (!failedIds.includes(selectedBotId()!)) {
            setTaskQueue([]);
            setChatCommands([]);
            setActiveTask(null);
            setChatLog([]);
            setConsoleLog([]);
            setDisconnectReason(null);
        }
    };

    const sendCommand = async () => {
        if (!command()) return;
        if (command().length > CHAT_MAX_LENGTH) {
            toast?.error(t('BOTENGINE_MSG_TOO_LONG', command().length, CHAT_MAX_LENGTH));
            return;
        }
        const res = await api.botLifecycle.sendCommand(selectedBotId()!, { command: command() });
        if (isOk(res.data)) {
            toast?.info(t('BOTENGINE_COMMAND_SENT'));
            setCommand('');
        } else {
            const msg = firstError(res.data)
                ?? firstError(res.data)
                ?? t('BOTENGINE_COMMAND_FAILED');
            toast?.error(msg);
        }
    };

    let qDropConfirmed = false;

    const handleInventoryMove = async (from: SlotRef, to: SlotRef) => {
        if (!selectedBotId()) return;
        const res = await api.botLifecycle.moveInventorySlot(selectedBotId()!, {
            fromArea: from.area,
            fromSlot: from.slot,
            toArea: to.area,
            toSlot: to.slot,
        });
        if (!isOk(res.data)) {
            const msg = firstError(res.data)
                ?? firstError(res.data)
                ?? t('BOTENGINE_INV_MOVE_FAILED');
            toast?.error(msg);
        }
    };

    const handleInventoryDrop = async (ref: SlotRef, wholeStack: boolean) => {
        if (!selectedBotId()) return;
        if (wholeStack) {
            if (!await showConfirm(t('BOTENGINE_INV_DROP_STACK_CONFIRM'), true)) return;
        } else if (!qDropConfirmed) {
            if (!await showConfirm(t('BOTENGINE_INV_DROP_CONFIRM'), true)) return;
            qDropConfirmed = true;
        }
        const res = await api.botLifecycle.dropInventorySlot(selectedBotId()!, {
            area: ref.area,
            slot: ref.slot,
            wholeStack,
        });
        if (!isOk(res.data)) {
            const msg = firstError(res.data)
                ?? firstError(res.data)
                ?? t('BOTENGINE_INV_DROP_FAILED');
            toast?.error(msg);
        }
    };

    const handleCloseWindow = async () => {
        if (!selectedBotId()) return;
        const res = await api.botLifecycle.closeInventoryWindow(selectedBotId()!);
        if (!isOk(res.data)) toast?.error(firstError(res.data) ?? t('BOTENGINE_INV_CLOSE_FAILED'));
    };

    const linkAccount = async () => {
        setAuthLoading(true);
        const res = await api.botAuth.initiateAuth(selectedBotId()!, { origin: window.location.origin });
        setAuthLoading(false);
        if (isOk(res.data)) {
            // State-cookie/nonce dance — MSLogin.tsx validates this cookie (mirrors Login.tsx).
            const nonce = Math.random().toString(36).substring(2);
            const cookieData = JSON.stringify({ next: '/botengine', backendState: (res.data.value as any).state });
            const expires = new Date();
            expires.setTime(expires.getTime() + 10 * 60 * 1000);
            document.cookie = `${nonce}=${encodeURIComponent(cookieData)};expires=${expires.toUTCString()};path=/`;

            const url = new URL((res.data.value as any).authorizationUrl);
            url.searchParams.set('state', nonce);
            window.location.href = url.toString();
        }
    };

    const unlinkAccount = async () => {
        if (!await showConfirm(t('BOTENGINE_UNLINK_CONFIRM') || 'Are you sure you want to unlink your Microsoft account?', true))
            return;
        setAuthLoading(true);
        const res = await api.botAuth.revokeAuth(selectedBotId()!);
        setAuthLoading(false);
        if (isOk(res.data)) {
            setSession({ linked: false });
            toast?.info(t('BOTENGINE_UNLINKED') || 'Account unlinked');
        }
    };

    const toggleKeepalive = async () => {
        const current = session().keepSessionAlive ?? false;
        setAuthLoading(true);
        const res = await api.botAuth.toggleKeepalive(selectedBotId()!, { enabled: !current });
        setAuthLoading(false);
        if (isOk(res.data)) {
            setSession(prev => ({ ...prev, keepSessionAlive: !current }));
        }
    };

    const forceRefreshToken = async () => {
        setAuthLoading(true);
        const res = await api.botAuth.forceRefreshAuth(selectedBotId()!);
        setAuthLoading(false);
        if (isOk(res.data)) {
            toast?.info(t('BOTENGINE_TOKEN_REFRESHED'));
            await fetchAuthStatus();
        } else {
            toast?.error(firstError(res.data) || t('BOTENGINE_REFRESH_FAILED_SHORT'));
        }
    };

    const statusClass = () => {
        const s = botStatus();
        if (s.state === 'Reconnecting') return styles.statusReconnecting;
        if (!s.running) return styles.statusOffline;
        if (s.state === 'Error') return styles.statusError;
        return styles.statusOnline;
    };

    const statusText = () => {
        const s = botStatus();
        if (s.state === 'Reconnecting') return t('BOTENGINE_STATUS_RECONNECTING');
        if (!s.running) return t('BOTENGINE_STATUS_OFFLINE');
        return s.state ?? t('BOTENGINE_STATUS_UNKNOWN');
    };

    // The bot's own connection to Minecraft and this page's connection to the backend fail
    // independently, so they get separate badges.
    const streamBadgeClass = () => {
        switch (streamState()) {
            case 'live': return styles.statusOnline;
            case 'connecting': return styles.statusReconnecting;
            default: return styles.statusError;
        }
    };

    const streamStateKey = () => {
        switch (streamState()) {
            case 'live': return 'BOTENGINE_STREAM_LIVE';
            case 'connecting': return 'BOTENGINE_STREAM_CONNECTING';
            default: return 'BOTENGINE_STREAM_OFFLINE';
        }
    };

    const statusIcon = (s: string) => {
        switch (s) {
            case 'Completed': return 'fa-solid fa-circle-check';
            case 'Failed': return 'fa-solid fa-circle-xmark';
            case 'Cancelled': return 'fa-solid fa-ban';
            default: return 'fa-solid fa-clock';
        }
    };

    const StatusIndicator = (props: { status: string; style?: any }) => (
        <Show
            when={props.status === 'Running'}
            fallback={<span class={styles.statusIcon}><i class={statusIcon(props.status)} style={props.style} /></span>}
        >
            <div class={styles.taskSpinner} />
        </Show>
    );

    const taskStatusColor = (s: string) => {
        switch (s) {
            case 'Running': return 'var(--primary-400)';
            case 'Completed': return 'var(--success-500)';
            case 'Failed': return 'var(--error-500)';
            case 'Cancelled': return 'var(--dark-text-muted)';
            default: return '#f59e0b';
        }
    };

    const taskStatusLabel = (status: string) => t(`BOTENGINE_TASK_STATUS_${status.toUpperCase()}`);

    const taskStatusClass = (s: string) => {
        switch (s) {
            case 'Running': return styles.taskStatusRunning;
            case 'Completed': return styles.taskStatusCompleted;
            case 'Failed': return styles.taskStatusFailed;
            case 'Cancelled': return styles.taskStatusCancelled;
            default: return styles.taskStatusPending;
        }
    };

    const taskRowAccentClass = (s: string) => {
        switch (s) {
            case 'Running': return styles.taskRowAccentRunning;
            case 'Completed': return styles.taskRowAccentCompleted;
            case 'Failed': return styles.taskRowAccentFailed;
            case 'Cancelled': return styles.taskRowAccentCancelled;
            default: return styles.taskRowAccentPending;
        }
    };

    const formatTime = (iso?: string | null) => fmtTime(iso, '-', true);
    const formatTaskTime = (iso?: string | null) =>
        iso && new Date(iso).toDateString() === new Date().toDateString() ? formatTime(iso) : formatDateTimeShort(iso, '-');

    const formatDuration = (startIso?: string | null, endIso?: string | null) => {
        if (!startIso) return '-';
        const start = new Date(startIso).getTime();
        if (Number.isNaN(start)) return '-';
        const end = endIso ? new Date(endIso).getTime() : Date.now();
        const ms = Math.max(0, end - start);
        if (ms < 1000) return `${ms} ms`;
        const s = Math.floor(ms / 1000);
        if (s < 60) return `${s}s`;
        const m = Math.floor(s / 60);
        const rs = s % 60;
        if (m < 60) return `${m}m ${rs}s`;
        const h = Math.floor(m / 60);
        return `${h}h ${m % 60}m`;
    };

    const [exportingTranscript, setExportingTranscript] = createSignal(false);
    const exportTaskTranscript = async (taskId: string) => {
        setExportingTranscript(true);
        const historyRoot = taskHistory().find(run => findTaskById(run, taskId));
        const rootQuery = historyRoot ? `?rootId=${encodeURIComponent(historyRoot.id)}` : '';
        const status = await downloadFromApi(
            `/api/botengine/${selectedBotId()}/tasks/history/${encodeURIComponent(taskId)}/export${rootQuery}`,
            `task-transcript-${taskId}.json`
        );
        setExportingTranscript(false);
        if (status !== 0) toast?.error(t('BOTENGINE_EXPORT_TRANSCRIPT_FAILED'));
    };

    const TaskDetailDialog = () => {
        const close = () => setSelectedTaskId(null);

        return (
            <Modal
                open={selectedTask() !== null}
                onClose={close}
                maxWidth="560px"
                leading={<Show when={selectedTask()}>{(t) => <StatusIndicator status={t().status} />}</Show>}
                title={<Show when={selectedTask()}>{(t) => <>{t().taskName}</>}</Show>}
                subtitle={<Show when={selectedTask()}>{(t) => <>{t().pluginName}</>}</Show>}
            >
                <Show when={selectedTask()}>
                    {(task) => (
                        <>
                            <div class={styles.taskDetailBadgeRow}>
                                <span class={`${styles.taskDetailBadge} ${taskStatusClass(task().status)}`}>
                                    {taskStatusLabel(task().status)}
                                </span>
                                <Show when={task().status === 'Running' && task().progress > 0}>
                                    <span class={styles.taskDetailProgressLabel}>{task().progress}%</span>
                                </Show>
                                <FeatureGate flag="EXPORT_TASK_TRANSCRIPT">
                                    <button
                                        type="button"
                                        class="btn btn-secondary btn-sm"
                                        style={{ 'margin-left': 'auto' }}
                                        disabled={exportingTranscript()}
                                        onClick={() => exportTaskTranscript(task().id)}
                                    >
                                        <Show when={!exportingTranscript()} fallback={<i class="fa-solid fa-spinner fa-spin" />}>
                                            <i class="fa-solid fa-download" />
                                        </Show>
                                        {' '}<Text val="BOTENGINE_EXPORT_TRANSCRIPT" />
                                    </button>
                                </FeatureGate>
                            </div>

                            <Show when={task().status === 'Running' && task().progress > 0}>
                                <div class={styles.taskDetailProgressBar}>
                                    <div class={styles.taskDetailProgressFill} style={{ width: `${task().progress}%` }} />
                                </div>
                            </Show>

                            <div class={styles.taskDetailInfoGrid}>
                                <div class={styles.taskDetailInfoItem}>
                                    <div class={styles.taskDetailInfoLabel}>Started</div>
                                    <div class={styles.taskDetailInfoValue}>{formatTaskTime(task().startedAt)}</div>
                                </div>
                                <div class={styles.taskDetailInfoItem}>
                                    <div class={styles.taskDetailInfoLabel}>Completed</div>
                                    <div class={styles.taskDetailInfoValue}>{formatTaskTime(task().completedAt)}</div>
                                </div>
                                <div class={styles.taskDetailInfoItem}>
                                    <div class={styles.taskDetailInfoLabel}>Duration</div>
                                    <div class={styles.taskDetailInfoValue}>
                                        {formatDuration(task().startedAt, task().completedAt)}
                                    </div>
                                </div>
                                <div class={styles.taskDetailInfoItem}>
                                    <div class={styles.taskDetailInfoLabel}>Task ID</div>
                                    <div class={`${styles.taskDetailInfoValue} ${styles.taskDetailMono}`}>
                                        {task().id.substring(0, 8)}...
                                    </div>
                                </div>
                            </div>

                            <Show when={task().statusMessage}>
                                <div class={styles.taskDetailSection}>
                                    <div class={styles.taskDetailSectionLabel}>Status</div>
                                    <div class={styles.taskDetailMessageBox}>{task().statusMessage}</div>
                                </div>
                            </Show>

                            <Show when={task().error}>
                                <div class={styles.taskDetailSection}>
                                    <div class={styles.taskDetailSectionLabel}>Error</div>
                                    <div class={styles.taskDetailErrorBox}>{task().error}</div>
                                </div>
                            </Show>

                            <Show when={task().subtasks.length > 0}>
                                <div class={styles.taskDetailSection}>
                                    <div class={styles.taskDetailSectionLabel}>
                                        Subtasks <span class={styles.sectionCount}>{task().subtasks.length}</span>
                                    </div>
                                    <div class={styles.taskDetailSubtasks}>
                                        <For each={task().subtasks}>{(sub) => <TaskNodeView node={sub} />}</For>
                                    </div>
                                </div>
                            </Show>
                        </>
                    )}
                </Show>
            </Modal>
        );
    };

    const TaskNodeView = (props: { node: TaskNode }) => {
      const hasSubtasks = () => props.node.subtasks.length > 0;
      const expanded = () => nodeExpanded(props.node);
      const stats = () => subtaskStats(props.node);
      return (
        <div class={styles.taskNode}>
            <div
                class={`${styles.taskRow} ${styles.taskRowTree} ${taskRowAccentClass(props.node.status)}`}
                role="button"
                tabIndex={0}
                onClick={() => setSelectedTaskId(props.node.id)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedTaskId(props.node.id);
                    }
                }}
            >
                <Show when={hasSubtasks()} fallback={<span class={styles.taskRowToggleSpacer} />}>
                    <button
                        type="button"
                        class={styles.taskRowToggle}
                        aria-expanded={expanded()}
                        title={t(expanded() ? 'BOTENGINE_TASK_COLLAPSE_SUBTASKS' : 'BOTENGINE_TASK_EXPAND_SUBTASKS')}
                        onClick={(e) => { e.stopPropagation(); toggleNode(props.node); }}
                    >
                        <i class={`fa-solid ${expanded() ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                    </button>
                </Show>
                <StatusIndicator status={props.node.status} />
                <div class={styles.taskRowMain}>
                    <div class={styles.taskRowTitle}>
                        <span class={styles.taskRowName}>{props.node.taskName}</span>
                        <span class={styles.taskRowPlugin}>{props.node.pluginName}</span>
                    </div>
                    <Show when={props.node.statusMessage}>
                        <div class={styles.taskRowMessage}>{props.node.statusMessage}</div>
                    </Show>
                    <Show when={props.node.error}>
                        <div class={styles.taskRowErrorInline} title={props.node.error}>
                            <i class="fa-solid fa-triangle-exclamation" /> {props.node.error}
                        </div>
                    </Show>
                </div>
                <div class={styles.taskRowMeta}>
                    <Show when={hasSubtasks()}>
                        <span
                            class={`${styles.taskRowSubCount} ${stats().failed ? styles.taskRowSubCountFailed : ''}`}
                            title={t('BOTENGINE_TASK_SUBTASKS_DONE', stats().done, stats().total)}
                        >
                            <i class="fa-solid fa-diagram-project" /> {stats().done}/{stats().total}
                        </span>
                    </Show>
                    <span class={`${styles.taskRowStatus} ${taskStatusClass(props.node.status)}`}>
                        {taskStatusLabel(props.node.status)}
                    </span>
                    <Show when={props.node.status === 'Running'}>
                        <span class={styles.taskRowDuration}>
                            {formatDuration(props.node.startedAt, null)}
                        </span>
                    </Show>
                    <Show when={props.node.completedAt}>
                        <span class={styles.taskRowDuration}>
                            {formatDuration(props.node.startedAt, props.node.completedAt)}
                        </span>
                    </Show>
                </div>
                <Show when={props.node.status === 'Running' && props.node.progress > 0}>
                    <div class={styles.taskRowProgressBar}>
                        <div class={styles.taskRowProgressFill} style={{ width: `${props.node.progress}%` }} />
                    </div>
                </Show>
            </div>
            <Show when={hasSubtasks() && expanded()}>
                <div class={styles.taskSubtasks}>
                    <For each={props.node.subtasks}>{(sub) =>
                        <TaskNodeView node={sub} />
                    }</For>
                </div>
            </Show>
        </div>
      );
    };

    return (
        <div class={styles.container}>
            <GroupScopeBar groupAware scopeKey="GROUP_SCOPE_LIFECYCLE" />
            <div class={styles.header}>
                <h1><Text val="BOTENGINE_TITLE" /></h1>
                <Show when={selectedBotId()}>
                    <span
                        class={`${styles.streamBadge} ${streamBadgeClass()}`}
                        title={lastLiveAt() ? t('BOTENGINE_STREAM_LAST_UPDATE', formatDateTime(new Date(lastLiveAt()!).toISOString(), '-')) : undefined}
                    >
                        <span class={styles.dot} />
                        <Text val={streamStateKey()} />
                    </span>
                </Show>
            </div>

            <Show when={streamState() === 'offline' && selectedBotId()}>
                <div class={styles.streamBanner}>
                    <i class="fa-solid fa-triangle-exclamation" />
                    <span><Text val="BOTENGINE_STREAM_OFFLINE_HINT" /></span>
                    <button class="btn btn-secondary btn-sm" onClick={() => reconnectBotEngineStream(selectedBotId()!)}>
                        <i class="fa-solid fa-rotate" /> <Text val="BOTENGINE_STREAM_RETRY" />
                    </button>
                </div>
            </Show>

            <Show when={!selectedBotId()}>
                <div class="fcard v" style={{ 'text-align': 'center', padding: '40px' }}>
                    <p style={{ color: 'var(--dark-text-muted)' }}>Select a bot above to continue.</p>
                </div>
            </Show>

            <Show when={selectedBotId()}>

            <div class={styles.authCard}>
                <h2><Text val="BOTENGINE_MC_ACCOUNT" /></h2>
                <div class={styles.authStatus}>
                    <Show when={session().linked} fallback={
                        <span class={styles.authNotLinked}>
                            <i class="fa-solid fa-circle-xmark" /> <Text val="BOTENGINE_NOT_LINKED" />
                        </span>
                    }>
                        <span class={styles.authLinked}>
                            <i class="fa-solid fa-circle-check" /> <Text val="BOTENGINE_LINKED" />
                        </span>
                    </Show>
                </div>

                <Show when={session().linked && session().refreshFailed}>
                    <div class={styles.refreshFailedBanner}>
                        <i class="fa-solid fa-triangle-exclamation" />
                        <div class={styles.refreshFailedBody}>
                            <span class={styles.refreshFailedTitle}>
                                <Text val="BOTENGINE_REFRESH_FAILED_TITLE" />
                            </span>
                            <span class={styles.refreshFailedReason}>
                                <Text val={session().refreshFailureReasonKey || 'AUTH_FAIL_UNKNOWN'} />
                            </span>
                            <Show when={session().refreshFailedAt}>
                                <span class={styles.refreshFailedMeta}>
                                    <Text val="BOTENGINE_REFRESH_FAILED_AT" /> {formatDateTime(session().refreshFailedAt, '-')}
                                </span>
                            </Show>
                            <Show when={session().refreshFailureDetail}>
                                <details>
                                    <summary class={styles.refreshFailedMeta}>
                                        <Text val="BOTENGINE_REFRESH_FAILED_DETAIL" />
                                    </summary>
                                    <div class={styles.refreshFailedDetail}>{session().refreshFailureDetail}</div>
                                </details>
                            </Show>
                        </div>
                    </div>
                </Show>

                <Show when={session().linked}>
                    <div class={styles.sessionInfo}>
                        <div class={styles.infoItem}>
                            <span class={styles.infoLabel}><Text val="BOTENGINE_USERNAME" /></span>
                            <span class={styles.infoValue}>{session().minecraftUsername}</span>
                        </div>
                        <div class={styles.infoItem}>
                            <span class={styles.infoLabel}><Text val="BOTENGINE_UUID" /></span>
                            <span class={styles.infoValue}>{session().minecraftUuid}</span>
                        </div>
                        <div class={styles.infoItem}>
                            <span class={styles.infoLabel}><Text val="BOTENGINE_TOKEN_EXPIRY" /></span>
                            <span class={`${styles.infoValue} ${session().isExpired ? styles.sessionExpired : styles.sessionActive}`}>
                                {session().isExpired ? <Text val="BOTENGINE_SESSION_EXPIRED" /> : expiryText()}
                            </span>
                        </div>
                        <div class={styles.infoItem}>
                            <span class={styles.infoLabel}><Text val="BOTENGINE_LAST_REFRESHED" /></span>
                            <span class={styles.infoValue}>
                                {formatDateTime(session().lastRefreshed, '-')}
                            </span>
                        </div>
                    </div>

                    <label class={styles.keepaliveToggle} onClick={(e) => { e.preventDefault(); toggleKeepalive(); }}>
                        <input type="checkbox" checked={session().keepSessionAlive ?? false} />
                        <div>
                            <span><Text val="BOTENGINE_KEEP_ALIVE" /></span>
                            <span class={styles.keepaliveDesc}><Text val="BOTENGINE_KEEP_ALIVE_DESC" /></span>
                        </div>
                    </label>
                </Show>

                <div class={styles.controls}>
                    <Show when={!session().linked}>
                        <button class="btn btn-primary" onClick={linkAccount} disabled={authLoading() || !canManageCredentials()}>
                            <i class="fa-brands fa-microsoft" /> <Text val="BOTENGINE_LINK_MS" />
                        </button>
                    </Show>
                    <Show when={session().linked}>
                        <button class="btn btn-secondary" onClick={forceRefreshToken} disabled={authLoading() || !canManageCredentials()}>
                            <i class="fa-solid fa-rotate" /> Force Refresh
                        </button>
                        <button class="btn btn-danger" onClick={unlinkAccount} disabled={authLoading() || !canManageCredentials()}>
                            <i class="fa-solid fa-unlink" /> <Text val="BOTENGINE_UNLINK" />
                        </button>
                    </Show>
                </div>
            </div>

            <div class={styles.authCard}>
                <h2><Text val="PROXY_SECTION_TITLE" /></h2>
                <ProxySelector botId={selectedBotId()} disabled={!canAssignProxy()} />
            </div>

            <div class={styles.controlCard}>
                <div class={styles.controlHeader}>
                    <h2><Text val="BOTENGINE_BOT_CONTROL" /></h2>
                    <span class={`${styles.statusBadge} ${statusClass()}`}>
                        <span class={styles.dot} />
                        {statusText()}
                    </span>
                </div>

                <Show when={botStatus().running}>
                    <div class={styles.botInfo}>
                        <div class={styles.infoItem}>
                            <span class={styles.infoLabel}><Text val="BOTENGINE_USERNAME" /></span>
                            <span class={styles.infoValue}>{botStatus().username}</span>
                        </div>
                        <div class={styles.infoItem}>
                            <span class={styles.infoLabel}><Text val="BOTENGINE_SERVER" /></span>
                            <span class={styles.infoValue}>{botStatus().server}</span>
                        </div>
                        <div class={styles.infoItem}>
                            <span class={styles.infoLabel}><Text val="BOTENGINE_PING" /></span>
                            <span class={styles.infoValue}>{botStatus().ping}ms</span>
                        </div>
                        <div class={styles.infoItem}>
                            <span class={styles.infoLabel}><Text val="BOTENGINE_CONNECTED_SINCE" /></span>
                            <span class={styles.infoValue}>
                                {formatDateTime(botStatus().connectedSince, '-')}
                            </span>
                        </div>
                    </div>
                </Show>

                <Show when={isReconnecting()}>
                    <div class={styles.botInfo}>
                        <div class={styles.infoItem}>
                            <span class={styles.infoLabel}><Text val="BOTENGINE_USERNAME" /></span>
                            <span class={styles.infoValue}>{botStatus().username}</span>
                        </div>
                        <div class={styles.infoItem}>
                            <span class={styles.infoLabel}><Text val="BOTENGINE_SERVER" /></span>
                            <span class={styles.infoValue}>{botStatus().server}</span>
                        </div>
                    </div>
                </Show>

                <Show when={disconnectReason() && !botStatus().running}>
                    <div style={{
                        background: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        'border-radius': 'var(--radius-md)',
                        padding: 'var(--spacing-sm) var(--spacing-md)',
                        'margin-bottom': 'var(--spacing-md)',
                        color: 'var(--error-500)',
                        'font-size': 'var(--font-size-sm)',
                        display: 'flex',
                        'align-items': 'center',
                        gap: 'var(--spacing-sm)',
                    }}>
                        <i class="fa-solid fa-circle-xmark" />
                        <span>{disconnectReason()}</span>
                    </div>
                </Show>

                <div class={styles.controls}>
                    <Show when={!botStatus().running && !isReconnecting() && !isConnecting()}>
                        <div class={styles.serverInput}>
                            <input
                                type="text"
                                placeholder={t('BOTENGINE_SERVER_ADDRESS_PLACEHOLDER')}
                                value={serverAddress()}
                                onInput={(e) => setServerAddress(e.currentTarget.value)}
                                onKeyDown={(e) => e.key === 'Enter' && startBot()}
                            />
                            <input
                                type="number"
                                placeholder={t('BOTENGINE_PORT_PLACEHOLDER')}
                                min={1}
                                max={65535}
                                value={serverPort()}
                                onInput={(e) => {
                                    const val = parseInt(e.currentTarget.value);
                                    setServerPort(isNaN(val) ? 25565 : Math.max(1, Math.min(65535, val)));
                                }}
                                style={{ width: '80px' }}
                            />
                            <button class="btn btn-primary" onClick={startBot} disabled={loading() || !session().linked || !canControl()}>
                                <i class="fa-solid fa-play" /> <Text val="BOTENGINE_START" />
                            </button>
                        </div>
                    </Show>
                    <Show when={botStatus().running || isReconnecting() || isConnecting()}>
                        <button class="btn btn-danger" onClick={stopBot} disabled={loading() || !canControl()}>
                            <i class="fa-solid fa-stop" /> <Text val="BOTENGINE_STOP" />
                        </button>
                    </Show>
                </div>
            </div>

            <Show when={shutoffIncidents().length > 0}>
                <div class="fcard v" style={{ gap: 'var(--spacing-md)', 'margin-bottom': 'var(--spacing-lg)' }}>
                    <Show when={openShutoffIncidents().length > 0}>
                        <h2 style={{ margin: 0 }}>
                            <i class="fa-solid fa-gavel" /> <Text val="BOTENGINE_SELF_SHUTOFF_TITLE" />
                        </h2>
                        <p class={styles.selfShutoffHint}><Text val="BOTENGINE_SELF_SHUTOFF_HINT" /></p>
                        <For each={openShutoffIncidents()}>{(incident) => shutoffIncidentRow(incident)}</For>
                    </Show>
                    <Show when={acknowledgedShutoffIncidents().length > 0}>
                        <details class={styles.selfShutoffHistory}>
                            <summary>
                                <i class="fa-solid fa-gavel" /> {t('BOTENGINE_SELF_SHUTOFF_HISTORY', String(acknowledgedShutoffIncidents().length))}
                            </summary>
                            <div class="v" style={{ gap: 'var(--spacing-sm)', 'margin-top': 'var(--spacing-sm)' }}>
                                <For each={acknowledgedShutoffIncidents()}>{(incident) => shutoffIncidentRow(incident)}</For>
                            </div>
                        </details>
                    </Show>
                </div>
            </Show>

            <Show when={botStatus().running || taskHistoryTimeline().length > 0}>
                <div class={styles.tabBar} role="tablist" aria-label="Bot Engine sections">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab() === 'live'}
                        class={`${styles.tabBtn} ${activeTab() === 'live' ? styles.tabBtnActive : ''}`}
                        onClick={() => setActiveTab('live')}
                    >
                        <i class="fa-solid fa-tv" /> Live
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab() === 'automation'}
                        class={`${styles.tabBtn} ${activeTab() === 'automation' ? styles.tabBtnActive : ''}`}
                        onClick={() => setActiveTab('automation')}
                    >
                        <i class="fa-solid fa-list-check" /> Automation
                    </button>
                </div>
            </Show>

            <Show when={activeTab() === 'live'}>

                <Show when={botStatus().running}>
                    <FeatureGate flag="LIVE_VIEW">
                        <LicenseGate type={GrantTypes.LicenseLiveView}>
                            <div class={styles.logCard}>
                                <h2
                                    style={{ cursor: 'pointer', 'user-select': 'none' }}
                                    onClick={() => setLiveViewOpen(v => !v)}
                                >
                                    <i class={`fa-solid ${liveViewOpen() ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                                    {' '}<i class="fa-solid fa-cube" /> Live View
                                </h2>
                                <Show when={liveViewOpen()}>
                                    <LiveView botId={selectedBotId()} />
                                </Show>
                            </div>
                        </LicenseGate>
                    </FeatureGate>
                </Show>

                <Show when={botStatus().running}>
                    <PlayerState state={playerState()} />
                </Show>

                <Show when={selectedBotId()}>
                    <ProfileInfoCard botId={selectedBotId()} />
                </Show>

                <Show when={botStatus().running}>
                    <BotInventory
                        snapshot={inventory()}
                        resolver={resolverReady()}
                        interactive={canWorldWrite()}
                        onMove={handleInventoryMove}
                        onDrop={handleInventoryDrop}
                        onCloseWindow={handleCloseWindow}
                    />
                </Show>

                <Show when={botStatus().running && canRunTasks()}>
                    <DumpInventoryPanel botId={selectedBotId()} snapshot={inventory()} disabled={!canRunTasks()} />
                </Show>

                <Show when={botStatus().running || chatLog().length > 0}>
                    <div class={styles.logCard}>
                        <div class="h" style={{ 'justify-content': 'space-between', 'align-items': 'center' }}>
                            <h2><i class="fa-solid fa-message" /> <Text val="BOTENGINE_CHAT_LOG" /></h2>
                            <Show when={can(mine(), BOT_ACTIONS.Read) && selectedBotId()}>
                                <a class="btn btn-secondary btn-sm" href={`/chatlogs?botId=${selectedBotId()}`}>
                                    <i class="fa-solid fa-clock-rotate-left" /> <Text val="BOT_CHATLOG_HISTORY_OPEN" />
                                </a>
                            </Show>
                        </div>
                        <ChatLog log={chatLog()} />
                    </div>
                </Show>

                <Show when={botStatus().running}>
                    <div class={styles.commandSection}>
                        <h2><Text val="BOTENGINE_SEND_COMMAND" /></h2>
                        <div class={styles.commandRow}>
                            <input
                                type="text"
                                placeholder={t('BOTENGINE_COMMAND_PLACEHOLDER')}
                                value={command()}
                                onInput={(e) => setCommand(e.currentTarget.value)}
                                onKeyDown={(e) => e.key === 'Enter' && sendCommand()}
                                aria-invalid={command().length > CHAT_MAX_LENGTH}
                            />
                            <button
                                class="btn btn-primary"
                                onClick={sendCommand}
                                disabled={!canWorldWrite() || command().length > CHAT_MAX_LENGTH}
                                title={command().length > CHAT_MAX_LENGTH
                                    ? `Message too long (${command().length}/${CHAT_MAX_LENGTH})`
                                    : undefined}
                            >
                                <i class="fa-solid fa-paper-plane" />
                            </button>
                        </div>
                        <div
                            style={{
                                'font-size': '11px',
                                'text-align': 'right',
                                'margin-top': '4px',
                                color: command().length > CHAT_MAX_LENGTH
                                    ? 'var(--error-500)'
                                    : command().length > CHAT_MAX_LENGTH - 10
                                        ? 'var(--color-warning-orange, #f59e0b)'
                                        : 'var(--dark-text-muted)',
                                'font-variant-numeric': 'tabular-nums',
                            }}
                        >
                            {command().length} / {CHAT_MAX_LENGTH}
                        </div>
                    </div>
                </Show>

                <Show when={botStatus().running || consoleLog().length > 0}>
                    <div class={styles.consoleCard}>
                        <h2><i class="fa-solid fa-terminal" /> Console</h2>
                        <div class={styles.consoleBody}>
                            <For each={consoleLog()}>{(line) =>
                                <p class={styles.consoleLine}>{line}</p>
                            }</For>
                        </div>
                    </div>
                </Show>
            </Show>

            <Show when={activeTab() === 'automation'}>

            <Show when={botStatus().running || taskHistoryTimeline().length > 0}>
                <div class={styles.taskCard}>
                    <div class={styles.taskCardHeader}>
                        <h2><i class="fa-solid fa-list-check" /> <Text val="BOTENGINE_TASKS" /></h2>
                        <Show when={activeTask() && ['Running', 'Pending'].includes(activeTask()!.status)}>
                            <div style={{ display: 'flex', gap: '8px', 'align-items': 'center' }}>
                                <Show when={activeTask()!.isPaused}>
                                    <span
                                        style={{
                                            background: 'var(--warning-500, #d97706)',
                                            color: '#fff',
                                            padding: '3px 10px',
                                            'border-radius': '12px',
                                            'font-size': '0.75rem',
                                            'font-weight': '600',
                                        }}
                                    >
                                        <i class="fa-solid fa-pause" style={{ 'margin-right': '4px' }} />
                                        {(activeTask()!.interceptDepth ?? 0) > 0
                                            ? t('BOTENGINE_TASK_PAUSED_INTERCEPT_DEPTH', activeTask()!.interceptDepth!)
                                            : t('BOTENGINE_TASK_PAUSED')}
                                    </span>
                                </Show>
                                <button
                                    class="btn btn-secondary"
                                    onClick={togglePause}
                                    disabled={pauseLoading()}
                                >
                                    <Show when={activeTask()!.isPaused} fallback={
                                        <><i class="fa-solid fa-pause" /> <Text val="BOTENGINE_TASK_PAUSE" /></>
                                    }>
                                        <i class="fa-solid fa-play" /> <Text val="BOTENGINE_TASK_RESUME" />
                                    </Show>
                                </button>
                                <button class="btn btn-danger" onClick={cancelTask} disabled={taskLoading()}>
                                    <i class="fa-solid fa-stop" /> <Text val="BOTENGINE_TASK_CANCEL" />
                                </button>
                            </div>
                        </Show>
                    </div>

                    <Show when={activeTask()?.interceptTask}>
                        <div class={styles.sectionLabel}>
                            <i class="fa-solid fa-bolt" style={{ 'margin-right': '6px', color: 'var(--warning-500, #d97706)' }} />
                            <Text val="BOTENGINE_TASK_INTERCEPT" />
                        </div>
                        <div class={styles.activeTaskSection}>
                            <TaskNodeView node={activeTask()!.interceptTask!} />
                        </div>
                    </Show>

                    <Show when={activeTask() && ['Running', 'Pending'].includes(activeTask()!.status)}>
                        <div class={styles.sectionLabel}>
                            <Text val="BOTENGINE_ACTIVE_TASK" />
                            <Show when={activeTask()!.interceptTask}>
                                <span style={{ 'margin-left': '8px', color: 'var(--dark-text-muted)', 'font-weight': 'normal', 'font-size': '0.85em' }}>
                                    <Text val="BOTENGINE_TASK_PAUSED_BY_INTERCEPT" />
                                </span>
                            </Show>
                            <Show when={activeTask()!.subtasks.length > 0}>
                                <div class={styles.treeControls}>
                                    <button
                                        type="button"
                                        class={styles.treeControlBtn}
                                        onClick={() => setSubtreeExpanded(activeTask(), true)}
                                    >
                                        <i class="fa-solid fa-angles-down" /> <Text val="BOTENGINE_TASK_EXPAND_ALL" />
                                    </button>
                                    <button
                                        type="button"
                                        class={styles.treeControlBtn}
                                        onClick={() => setSubtreeExpanded(activeTask(), false)}
                                    >
                                        <i class="fa-solid fa-angles-up" /> <Text val="BOTENGINE_TASK_COLLAPSE_ALL" />
                                    </button>
                                </div>
                            </Show>
                        </div>
                        <div
                            class={styles.activeTaskSection}
                            style={activeTask()!.interceptTask ? { opacity: '0.55' } : undefined}
                        >
                            <TaskNodeView node={activeTask()!} />
                        </div>
                    </Show>

                    <Show when={chatCommands().length > 0}>
                        <div class={styles.sectionLabel}>
                            <i class="fa-solid fa-terminal" style={{ 'margin-right': '6px', color: 'var(--primary-400)' }} />
                            <Text val="BOTENGINE_CHAT_COMMANDS" />
                            <span class={styles.sectionCount}>{chatCommands().length}</span>
                        </div>
                        <div class={styles.activeTaskSection}>
                            <For each={chatCommands()}>{(node) => <TaskNodeView node={node} />}</For>
                        </div>
                    </Show>

                    <Show when={taskQueue().length > 0}>
                        <div class={styles.sectionLabel}>
                            <Text val="TASK_QUEUE" />
                            <span class={styles.sectionCount}>{taskQueue().length}</span>
                        </div>
                        <div class={styles.queueSection}>
                            <For each={taskQueue()}>{(item, index) =>
                                <div class={`${styles.taskRow} ${styles.taskRowTree} ${styles.taskRowAccentPending} ${styles.taskRowNonInteractive}`}>
                                    <span class={styles.queueIndex}>{index() + 1}</span>
                                    <i class="fa-solid fa-clock" style={{ color: 'var(--dark-text-muted)' }} />
                                    <div class={styles.taskRowMain}>
                                        <div class={styles.taskRowTitle}>
                                            <span class={styles.taskRowName}>{item.taskName}</span>
                                            <span class={styles.taskRowPlugin}>{item.pluginName}</span>
                                        </div>
                                    </div>
                                    <div class={styles.taskRowMeta}>
                                        <span class={`${styles.taskRowStatus} ${styles.taskStatusPending}`}><Text val="BOTENGINE_TASK_QUEUED" /></span>
                                        <button
                                            class="btn btn-secondary btn-sm"
                                            disabled={!canRunTasks()}
                                            title={t('BOTENGINE_TASK_CANCEL_QUEUED')}
                                            onClick={() => cancelQueuedTask(item.id)}
                                        >
                                            <i class="fa-solid fa-xmark" />
                                        </button>
                                    </div>
                                </div>
                            }</For>
                        </div>
                    </Show>

                    <div class={styles.sectionLabel}>
                        <Text val="BOTENGINE_TASK_HISTORY" />
                        <Show when={taskHistory().length > 0}>
                            <span class={styles.sectionCount}>{taskHistory().length}{taskHistoryNextBeforeId() ? '+' : ''}</span>
                        </Show>
                    </div>
                    <Show when={taskHistoryTimeline().length > 0} fallback={
                        <Switch>
                            <Match when={taskHistoryPage.loading}>
                                <div class={styles.historySection}><Skeleton variant="row" count={3} /></div>
                            </Match>
                            <Match when={taskHistoryPage.state === 'errored'}>
                                <EmptyState
                                    icon="fa-triangle-exclamation"
                                    title={t('BOTENGINE_TASK_HISTORY_LOAD_FAILED')}
                                    action={<button type="button" class="btn btn-secondary btn-sm" onClick={() => void retryTaskHistoryPage()}><Text val="RETRY" /></button>}
                                />
                            </Match>
                            <Match when={taskHistoryPage.state === 'ready'}>
                                <EmptyState icon="fa-clock-rotate-left" title={t('BOTENGINE_TASK_HISTORY_EMPTY')} />
                            </Match>
                        </Switch>
                    }>
                        <div class={styles.historySection}>
                            <For each={taskHistoryTimeline()}>{(entry) => isConnectionMarker(entry)
                                ? <div
                                    class={`${styles.connectionMarker} ${entry.online ? styles.connectionMarkerOnline : styles.connectionMarkerOffline}`}
                                    title={formatDateTime(entry.at)}
                                >
                                    <i class={entry.online ? 'fa-solid fa-plug-circle-check' : 'fa-solid fa-plug-circle-xmark'} />
                                    <span class={styles.connectionMarkerLabel}>
                                        <Text val={`BOT_AUDIT_ACTION_${entry.actionType.toUpperCase()}`} />
                                    </span>
                                    <Show when={entry.actorUsername}>
                                        <span class={styles.connectionMarkerDetail}>{t('BOTENGINE_TASK_HISTORY_MARKER_BY', entry.actorUsername!)}</span>
                                    </Show>
                                    <Show when={entry.reason}>
                                        <span class={styles.connectionMarkerDetail} title={entry.reason!}>{entry.reason}</span>
                                    </Show>
                                    <span class={styles.taskRowTime}>{formatTaskTime(entry.at)}</span>
                                </div>
                                : <div
                                    class={`${styles.taskRow} ${taskRowAccentClass(entry.status)}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => setSelectedTaskId(entry.id)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setSelectedTaskId(entry.id);
                                        }
                                    }}
                                >
                                    <StatusIndicator status={entry.status} style={{ color: taskStatusColor(entry.status) }} />
                                    <div class={styles.taskRowMain}>
                                        <div class={styles.taskRowTitle}>
                                            <span class={styles.taskRowName}>{entry.taskName}</span>
                                            <span class={styles.taskRowPlugin}>{entry.pluginName}</span>
                                        </div>
                                        <Show when={entry.error}>
                                            <div class={styles.taskRowErrorInline} title={entry.error}>
                                                <i class="fa-solid fa-triangle-exclamation" /> {entry.error}
                                            </div>
                                        </Show>
                                    </div>
                                    <div class={styles.taskRowMeta}>
                                        <span class={`${styles.taskRowStatus} ${taskStatusClass(entry.status)}`}>{taskStatusLabel(entry.status)}</span>
                                        <Show when={entry.completedAt}>
                                            <span class={styles.taskRowDuration}>
                                                {formatDuration(entry.startedAt, entry.completedAt)}
                                            </span>
                                        </Show>
                                        <Show when={entry.completedAt}>
                                            <span class={styles.taskRowTime}>{formatTaskTime(entry.completedAt)}</span>
                                        </Show>
                                    </div>
                                </div>
                            }</For>
                            <Show when={taskHistoryNextBeforeId()}>
                                <div class={styles.historyLoadOlder}>
                                    <button
                                        type="button"
                                        class="btn btn-secondary btn-sm"
                                        disabled={taskHistoryPage.loading}
                                        onClick={loadOlderTaskHistory}
                                    >
                                        <Show when={taskHistoryPage.loading} fallback={<i class="fa-solid fa-angles-down" />}>
                                            <i class="fa-solid fa-spinner fa-spin" />
                                        </Show>
                                        {' '}<Text val="BOTENGINE_TASK_HISTORY_LOAD_OLDER" />
                                    </button>
                                </div>
                            </Show>
                        </div>
                    </Show>

                    <FeatureGate flag="AUTO_RESUME">
                    <div class="fcard v" style={{ gap: 'var(--spacing-md)', "margin-bottom": 'var(--spacing-lg)' }}>
                        <div class={styles.sectionLabel}><Text val="BOTENGINE_RESUME_AFTER" /></div>
                        <select
                            value={resumeBehavior()}
                            onChange={(e) => setResumeBehavior(parseInt(e.currentTarget.value) as 0 | 1 | 2)}
                            disabled={resumeSaving()}
                        >
                            <option value={0}>{t('BOTENGINE_RESUME_OFF') || 'Off'}</option>
                            <option value={1}>{t('BOTENGINE_RESUME_PRIOR') || 'Resume previous task'}</option>
                            <option value={2}>{t('BOTENGINE_RESUME_PINNED') || 'Run a fixed task'}</option>
                        </select>

                        <Show when={resumeBehavior() === 1}>
                            <div style={{ "font-size": 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                                <Show when={resumeLastKey()} fallback={<Text val="BOTENGINE_RESUME_NO_PRIOR" />}>
                                    <Text val="BOTENGINE_RESUME_LAST_SEEN" />: <code>{resumeLastKey()}</code>
                                    <Show when={resumeLastAt()}>
                                        <span> ({formatTime(resumeLastAt()!)})</span>
                                    </Show>
                                </Show>
                            </div>
                        </Show>

                        <Show when={resumeBehavior() === 2}>
                            <select
                                ref={(el) => {
                                    createEffect(() => {
                                        availableTasks();
                                        const v = resumePinnedKey();
                                        if (el.value !== v) el.value = v;
                                    });
                                }}
                                value={resumePinnedKey()}
                                onChange={(e) => { setResumePinnedKey(e.currentTarget.value); setResumePinnedArgs({}); }}
                                disabled={resumeSaving()}
                            >
                                <option value="">{t('BOTENGINE_RESUME_PICK_TASK') || '-- pick a task --'}</option>
                                <For each={availableTasks()}>{(pg) =>
                                    <For each={pg.tasks}>{(task) =>
                                        <option value={`${pg.plugin}:${task.name}`}>{pg.plugin}: {task.name}</option>
                                    }</For>
                                }</For>
                            </select>

                            <Show when={(() => {
                                const k = resumePinnedKey();
                                if (!k) return null;
                                const [plugin, taskName] = k.split(':');
                                const pg = availableTasks().find(p => p.plugin === plugin);
                                const task = pg?.tasks.find(t => t.name === taskName);
                                return task ?? null;
                            })()} keyed>
                                {(task) => (
                                    <Show when={task.parameters.length > 0}>
                                        <div class={styles.taskParams}>
                                            <For each={task.parameters}>{(param) =>
                                                <div class={styles.paramRow}>
                                                    <label title={param.description}>
                                                        {param.name}
                                                        {param.required ? ' *' : ''}
                                                    </label>
                                                    <TaskParameterInput
                                                        botId={selectedBotId()}
                                                        parameter={param}
                                                        value={resumePinnedArgs()[param.name] ?? ''}
                                                        onChange={(value) => setResumePinnedArg(param.name, value)}
                                                    />
                                                </div>
                                            }</For>
                                        </div>
                                    </Show>
                                )}
                            </Show>
                        </Show>

                        <div style={{ display: 'flex', "justify-content": 'flex-end' }}>
                            <button
                                class="btn btn-primary"
                                onClick={saveResumeConfig}
                                disabled={resumeSaving() || (resumeBehavior() === 2 && !resumePinnedKey())}
                            >
                                <Text val="SAVE" />
                            </button>
                        </div>
                    </div>
                    </FeatureGate>

                    <Show when={availableTasks().length > 0} fallback={
                        <div class={styles.taskEmpty}><Text val="BOTENGINE_NO_TASKS" /></div>
                    }>
                        <div class={styles.sectionLabel}><Text val="BOTENGINE_AVAILABLE_TASKS" /></div>
                        <For each={availableTasks()}>{(pg) =>
                            <div class={styles.pluginGroup}>
                                <button
                                    class={styles.pluginHeader}
                                    onClick={() => setExpandedPlugin(expandedPlugin() === pg.plugin ? null : pg.plugin)}
                                >
                                    <i class={`fa-solid fa-chevron-${expandedPlugin() === pg.plugin ? 'down' : 'right'}`} />
                                    {pg.plugin}
                                    <span class={styles.taskCount}>({pg.tasks.length})</span>
                                </button>
                                <Show when={expandedPlugin() === pg.plugin}>
                                    <For each={pg.tasks}>{(task) => {
                                        const key = () => `${pg.plugin}:${task.name}`;
                                        const isActive = () => { const t = activeTask(); return t != null && ['Running', 'Pending'].includes(t.status); };
                                        return (
                                            <div class={styles.taskEntry}>
                                                <div class={styles.taskEntryHeader}>
                                                    <div>
                                                        <div class={styles.taskEntryName}>{task.name}</div>
                                                        <div class={styles.taskEntryDesc}>{task.description}</div>
                                                    </div>
                                                    <div class={styles.taskEntryButtons}>
                                                        <button
                                                            class="btn btn-primary"
                                                            onClick={() => startTask(pg.plugin, task.name)}
                                                            disabled={taskLoading() || !!isActive()}
                                                            style={{ "white-space": "nowrap" }}
                                                        >
                                                            <i class="fa-solid fa-play" /> <Text val="BOTENGINE_TASK_RUN" />
                                                        </button>
                                                        <button
                                                            class="btn btn-secondary"
                                                            onClick={() => enqueueTask(pg.plugin, task.name)}
                                                            disabled={taskLoading()}
                                                            style={{ "white-space": "nowrap" }}
                                                            title="Add to queue"
                                                        >
                                                            <i class="fa-solid fa-plus" /> Queue
                                                        </button>
                                                    </div>
                                                </div>
                                                <Show when={task.parameters.length > 0}>
                                                    <div class={styles.taskParams}>
                                                        <For each={task.parameters}>{(param) =>
                                                            <div class={styles.paramRow}>
                                                                <label title={param.description}>
                                                                    {param.name}
                                                                    {param.required ? ' *' : ''}
                                                                </label>
                                                                <TaskParameterInput
                                                                    botId={selectedBotId()}
                                                                    parameter={param}
                                                                    value={(taskArgs()[key()] ?? {})[param.name] ?? ''}
                                                                    onChange={(value) => setTaskArg(pg.plugin, task.name, param.name, value)}
                                                                />
                                                            </div>
                                                        }</For>
                                                    </div>
                                                </Show>
                                            </div>
                                        );
                                    }}</For>
                                </Show>
                            </div>
                        }</For>
                    </Show>
                </div>

                <TaskDetailDialog />
            </Show>

            </Show>

            </Show>
        </div>
    );
}
